// @file Network requests related to whiteboard AI features

import environment from '@@/bits/environment'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import FetchableObject from '@@/surface/api/fetchable_object'
import { getApiFetchCommonOptions } from '@@/surface/api_fetch'
import type { FetchOptions } from '@padlet/fetch'
import { HTTPMethod } from '@padlet/fetch'

export enum WhiteboardAiActionType {
  CreateShapes = 'create_shapes',
  SetCardBackground = 'set_card_background',
  SearchAiImage = 'search_ai_image',
  ConvertTextToSpeech = 'convert_text_to_speech',
}

// Base type for all action payloads
export interface ActionPayload {
  message: string
}

// Payload for create_shape action
export interface CreateShapesPayload extends ActionPayload {
  sound_effects: Record<string, string>
  visual_effects: Record<string, string>
  stickers: Record<string, string>
}

// Payload for set_card_background action
export interface SetCardBackgroundPayload extends ActionPayload {
  card_background_id: string
}

interface WhiteboardAiActionCreateShapes {
  type: 'create_shapes'
  payload: CreateShapesPayload
}

interface WhiteboardAiActionSetCardBackground {
  type: 'set_card_background'
  payload: SetCardBackgroundPayload
}

interface WhiteboardAiActionSearchAiImage {
  type: 'search_ai_image'
  payload: ActionPayload
}

interface WhiteboardAiActionConvertTextToSpeech {
  type: 'convert_text_to_speech'
  payload: ActionPayload
}

export type WhiteboardAiAction =
  | WhiteboardAiActionCreateShapes
  | WhiteboardAiActionSetCardBackground
  | WhiteboardAiActionSearchAiImage
  | WhiteboardAiActionConvertTextToSpeech

export interface WhiteboardAiAttributes {
  long_description_of_strategy: string
  actions: WhiteboardAiAction[]
}

interface WhiteboardAiActionResponse {
  data: {
    type: 'whiteboard_ai_classify_actions'
    attributes: WhiteboardAiAttributes
  }
}

interface TranscriptionResponse {
  data: {
    attributes: {
      transcription: string
    }
  }
}

class WhiteboardAiApi extends FetchableObject {
  public static get baseUrl(): string {
    return environment === 'production' ? 'https://morpheus.padlet.com' : 'https://morpheus.padlet.dev'
  }

  public static async classifyActions(
    message: string,
    fetchOptions: FetchOptions = {},
  ): Promise<WhiteboardAiAttributes> {
    const authorization = fetchOptions.authorization?.join(' ') ?? ''
    const response = await fetch(`${this.baseUrl}/api/v1/whiteboard-ai/classify-actions`, {
      method: HTTPMethod.post,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authorization,
      },
      body: asciiSafeStringify({
        message,
      }),
      ...fetchOptions,
    })
    const result = (await response.json()) as WhiteboardAiActionResponse
    return result.data.attributes
  }

  public static async transcribeAudio(audioFile: File): Promise<string> {
    const formData = new FormData()
    formData.append('file', audioFile)

    const data = await fetch('/api/1/transcriptions', {
      method: HTTPMethod.post,
      body: formData,
      ...getApiFetchCommonOptions(),
    })
    const json = (await data.json()) as TranscriptionResponse

    return json.data.attributes.transcription
  }
}

export { WhiteboardAiApi }
